import type {ResultadoDeRevisionDeCarpetaDeExportaciones} from '@cnc/contracts';
import {useQueryClient} from '@tanstack/react-query';
import {useState} from 'react';
import {Link} from 'react-router';
import {
  revisarCarpetaDeExportaciones,
  rutaEnLaCarpetaDeExportaciones,
  subirArchivoDeCarpetaDeExportaciones,
} from '../api/documentos';
import {tr} from '../i18n/idioma';
import {numero} from '../utilidades/formato';
import {PistaDeSoltar, ZonaParaSoltar} from './zona-para-soltar';

// Archivos que se suben a la vez: suficiente para ir rapido sin ahogar al servidor.
const A_LA_VEZ = 3;

type Avance = {
  total: number;
  hechos: number;
  cargados: number;
  yaEstaban: number;
  ignorados: number;
  facturas: number;
  errores: string[];
};

// Cargar archivos → Carpeta de exportaciones (decision 0070): se elige la
// carpeta Exports de Dropbox (o la de una sola exportacion); se revisa sin
// guardar nada y despues se sube cada archivo al expediente de su exportacion.
export function CargaDeCarpetaDeExportaciones({onCerrar}: {onCerrar: () => void}) {
  const cliente = useQueryClient();
  const [archivos, setArchivos] = useState<File[]>([]);
  const [carpeta, setCarpeta] = useState('');
  const [revision, setRevision] = useState<ResultadoDeRevisionDeCarpetaDeExportaciones | null>(null);
  const [revisando, setRevisando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avance, setAvance] = useState<Avance | null>(null);
  const [subiendo, setSubiendo] = useState(false);

  const elegir = async (lista: File[]) => {
    setArchivos(lista);
    setRevision(null);
    setAvance(null);
    setError(null);
    if (!lista.length) return;
    setCarpeta((lista[0]!.webkitRelativePath || '').split('/')[0] ?? '');
    setRevisando(true);
    try {
      setRevision(await revisarCarpetaDeExportaciones(lista));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRevisando(false);
    }
  };

  const cargar = async () => {
    if (!revision) return;
    const porSubir = new Set(revision.porSubir);
    const cola = archivos.filter(a => porSubir.has(rutaEnLaCarpetaDeExportaciones(a)));
    const estado: Avance = {
      total: cola.length,
      hechos: 0,
      cargados: 0,
      yaEstaban: 0,
      ignorados: 0,
      facturas: 0,
      errores: [],
    };
    setAvance({...estado});
    setSubiendo(true);
    let siguiente = 0;
    const trabajador = async () => {
      while (siguiente < cola.length) {
        const archivo = cola[siguiente++]!;
        try {
          const r = await subirArchivoDeCarpetaDeExportaciones(archivo);
          if (r.resultado === 'cargado') estado.cargados++;
          else if (r.resultado === 'ya_estaba') estado.yaEstaban++;
          else estado.ignorados++;
          estado.facturas += r.facturas;
        } catch (e) {
          estado.errores.push(
            `${rutaEnLaCarpetaDeExportaciones(archivo)}: ${e instanceof Error ? e.message : String(e)}`,
          );
        }
        estado.hechos++;
        setAvance({...estado, errores: [...estado.errores]});
      }
    };
    await Promise.all(Array.from({length: A_LA_VEZ}, trabajador));
    setSubiendo(false);
    void cliente.invalidateQueries({queryKey: ['seguimiento']});
    void cliente.invalidateQueries({queryKey: ['certificados']});
  };

  const porcentaje = avance && avance.total ? Math.round((avance.hechos / avance.total) * 100) : 0;
  return (
    <div className="space-y-4" data-prueba="carga-carpeta-de-exportaciones">
      <div className="rounded-lg border border-linea bg-white p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-medium">{tr('Cargar la carpeta de exportaciones', 'Load the exports folder')}</h2>
          <button type="button" onClick={onCerrar} className="text-xs text-apagado hover:underline">
            {tr('Cerrar', 'Close')}
          </button>
        </div>
        <p className="mt-1 max-w-3xl text-sm text-apagado">
          {tr(
            'Elige la carpeta de las exportaciones de Dropbox (todas) o la de una sola exportación. Cada archivo va al expediente de la exportación de su carpeta: controles de empaque, Departure Report, listas de empaque, facturas, BL, certificados y lo demás. Primero se revisa sin guardar nada; después se suben solo los que faltan. Lo que ya está en el expediente (mismo nombre y tamaño) no se repite. Las facturas electrónicas en PDF se leen y quedan ligadas a sus Big Bags. La carpeta de Dropbox no se modifica.',
            "Choose the Dropbox exports folder (all of them) or the folder of a single export. Each file goes to the export file of its folder's export: packing controls, Departure Report, packing lists, invoices, BLs, certificates and the rest. It is reviewed first without saving anything; then only missing files are uploaded. What is already in the export file (same name and size) is not repeated. Electronic invoices in PDF are read and linked to their Big Bags. The Dropbox folder is not changed.",
          )}
        </p>
        <ZonaParaSoltar
          carpeta
          deshabilitado={subiendo || revisando}
          onSoltar={lista => void elegir(lista)}
          className="mt-3 flex flex-wrap items-center gap-3"
        >
          <label
            className={`inline-flex cursor-pointer items-center gap-1 rounded-md border border-verde bg-white px-3 py-1.5 text-sm text-verde-oscuro hover:bg-verde-claro ${subiendo || revisando ? 'pointer-events-none opacity-50' : ''}`}
          >
            📂 {tr('Elegir carpeta', 'Choose folder')}
            <input
              type="file"
              multiple
              aria-label={tr('Carpeta de exportaciones', 'Exports folder')}
              className="sr-only"
              {...{webkitdirectory: '', directory: ''}}
              onChange={e => {
                void elegir([...(e.target.files ?? [])]);
                e.target.value = '';
              }}
            />
          </label>
          {carpeta && (
            <span className="text-sm text-apagado">
              {tr(`${carpeta}: ${numero(archivos.length)} archivos`, `${carpeta}: ${numero(archivos.length)} files`)}
            </span>
          )}
          {!carpeta && !revisando && <PistaDeSoltar carpeta />}
          {revisando && <span className="text-sm text-apagado">{tr('Revisando…', 'Reviewing…')}</span>}
        </ZonaParaSoltar>
        {error && <p className="mt-2 text-sm text-alerta">{error}</p>}
      </div>

      {revision && (
        <div
          className="space-y-3 rounded-lg border border-linea bg-white p-4"
          data-prueba="revision-carpeta-exportaciones"
        >
          <h3 className="text-sm font-semibold tracking-wide text-apagado uppercase">
            {tr('Lo que se cargaría', 'What would be loaded')}
          </h3>
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-4">
            <Cifra titulo={tr('Archivos en la carpeta', 'Files in the folder')} valor={revision.archivos} />
            <Cifra titulo={tr('Por cargar', 'To load')} valor={revision.nuevos} fuerte />
            <Cifra
              titulo={tr('Ya estaban en el expediente', 'Already in the export file')}
              valor={revision.yaCargados}
            />
            <Cifra titulo={tr('Se ignoran', 'Ignored')} valor={revision.ignoradosTotal} />
          </dl>
          <div className="flex flex-wrap gap-2 text-xs">
            {revision.porCategoria.map(c => (
              <span key={c.categoria} className="rounded-full border border-linea px-2 py-0.5">
                {c.etiqueta}: {numero(c.archivos)}
              </span>
            ))}
          </div>
          <details>
            <summary className="cursor-pointer text-sm text-verde">
              {tr(
                `Ver las ${numero(revision.porExportacion.length)} exportaciones`,
                `See the ${numero(revision.porExportacion.length)} exports`,
              )}
            </summary>
            <table className="mt-2 w-full max-w-xl text-sm">
              <thead>
                <tr className="text-left text-xs text-apagado">
                  <th className="py-1 pr-2 font-semibold">{tr('Exportación', 'Export')}</th>
                  <th className="py-1 pr-2 text-right font-semibold">{tr('Archivos', 'Files')}</th>
                  <th className="py-1 text-right font-semibold">{tr('Por cargar', 'To load')}</th>
                </tr>
              </thead>
              <tbody>
                {revision.porExportacion.map(e => (
                  <tr key={e.codigo} className="border-t border-linea/70">
                    <td className="py-1 pr-2">
                      <Link to={`/seguimiento/${encodeURIComponent(e.codigo)}`} className="text-verde hover:underline">
                        {e.codigo}
                      </Link>
                    </td>
                    <td className="py-1 pr-2 text-right tabular-nums">{numero(e.archivos)}</td>
                    <td className="py-1 text-right tabular-nums">{numero(e.nuevos)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
          {revision.ignorados.length > 0 && (
            <details>
              <summary className="cursor-pointer text-sm text-verde">
                {tr(
                  `Ver los ${numero(revision.ignoradosTotal)} ignorados`,
                  `See the ${numero(revision.ignoradosTotal)} ignored`,
                )}
              </summary>
              <ul className="mt-1 space-y-0.5 text-xs">
                {revision.ignorados.map(i => (
                  <li key={i.ruta} className="break-all">
                    {i.ruta} — <span className="text-apagado">{i.motivo}</span>
                  </li>
                ))}
              </ul>
              {revision.ignoradosTotal > revision.ignorados.length && (
                <p className="mt-1 text-xs text-apagado">
                  {tr(
                    `Se muestran los primeros ${numero(revision.ignorados.length)}.`,
                    `The first ${numero(revision.ignorados.length)} are shown.`,
                  )}
                </p>
              )}
            </details>
          )}
          {!avance && (
            <button
              type="button"
              disabled={!revision.nuevos}
              onClick={() => void cargar()}
              className="rounded-md bg-verde px-4 py-2 text-sm text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              {revision.nuevos
                ? tr(`⇪ Cargar ${numero(revision.nuevos)} archivos`, `⇪ Load ${numero(revision.nuevos)} files`)
                : tr('No hay nada nuevo para cargar', 'Nothing new to load')}
            </button>
          )}
        </div>
      )}

      {avance && (
        <div
          className="space-y-2 rounded-lg border border-linea bg-white p-4"
          role="status"
          data-prueba="avance-carpeta-exportaciones"
        >
          <div className="flex items-baseline justify-between text-sm">
            <span className="font-medium">
              {subiendo ? tr('Cargando…', 'Loading…') : tr('Carga terminada. ✓', 'Load finished. ✓')}
            </span>
            <span className="tabular-nums text-apagado">
              {numero(avance.hechos)} / {numero(avance.total)} ({porcentaje} %)
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-papel">
            <div className="h-full bg-verde transition-all" style={{width: `${porcentaje}%`}} />
          </div>
          <p className="text-xs text-apagado">
            {tr(
              `Cargados: ${numero(avance.cargados)} · ya estaban: ${numero(avance.yaEstaban)} · ignorados: ${numero(avance.ignorados)} · facturas leídas: ${numero(avance.facturas)} · con error: ${numero(avance.errores.length)}`,
              `Loaded: ${numero(avance.cargados)} · already there: ${numero(avance.yaEstaban)} · ignored: ${numero(avance.ignorados)} · invoices read: ${numero(avance.facturas)} · with errors: ${numero(avance.errores.length)}`,
            )}
          </p>
          {subiendo && (
            <p className="text-xs text-apagado">
              {tr(
                'No cierres esta pestaña hasta que termine. Si se interrumpe, vuelve a elegir la carpeta: lo cargado no se repite.',
                'Do not close this tab until it finishes. If interrupted, choose the folder again: what was loaded is not repeated.',
              )}
            </p>
          )}
          {avance.errores.length > 0 && (
            <ul className="space-y-0.5 text-xs text-alerta">
              {avance.errores.slice(0, 20).map(e => (
                <li key={e} className="break-all">
                  {e}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Cifra({titulo, valor, fuerte}: {titulo: string; valor: number; fuerte?: boolean}) {
  return (
    <div className="flex justify-between gap-2 border-b border-linea/50 py-0.5">
      <dt className="text-apagado">{titulo}</dt>
      <dd className={`tabular-nums ${fuerte ? 'font-semibold' : ''}`}>{numero(valor)}</dd>
    </div>
  );
}
